/**
 * Color utility functions for theme-aware color adjustments.
 */

/**
 * Converts a hex color to HSL components.
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Converts HSL values to a hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Adjusts a color for dark mode by reducing lightness and slightly reducing saturation.
 * This makes bright colors more muted and easier on the eyes in dark mode.
 *
 * @param hexColor - The hex color to adjust (e.g., '#F06423')
 * @param isDarkMode - Whether dark mode is active
 * @returns The adjusted hex color
 */
export function adjustColorForDarkMode(hexColor: string, isDarkMode: boolean): string {
  if (!isDarkMode || !hexColor) return hexColor;

  try {
    const hsl = hexToHsl(hexColor);

    // For dark mode:
    // - Reduce lightness to around 35-45% (darker but still visible)
    // - Slightly reduce saturation for a more muted look
    const adjustedL = Math.min(hsl.l, 45); // Cap lightness at 45%
    const adjustedS = hsl.s * 0.85; // Reduce saturation by 15%

    return hslToHex(hsl.h, adjustedS, adjustedL);
  } catch {
    // If color parsing fails, return original
    return hexColor;
  }
}

/**
 * Determines if text should be light or dark based on background color.
 * Uses relative luminance calculation.
 *
 * @param hexColor - The background hex color
 * @returns 'light' if text should be light, 'dark' if text should be dark
 */
export function getContrastTextColor(hexColor: string): 'light' | 'dark' {
  if (!hexColor) return 'dark';

  try {
    const hex = hexColor.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}
